import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';
import { defineAsyncComponent } from 'vue';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';

import { DASHBOARD_ROUTE_NAMES } from '@/app/dashboards/constants';
import { prefetchLandingPage } from '@/app/landingPage/prefetch';
import enumTypes from '@/app/library/constants';
import { trackSubscriptionUpdated } from '@/app/settings/mixpanel';
import {
  brandSwitchTarget,
  runBeforeBrandSwitchGuards,
  setNavigationBrandSwitch,
  useBrandSwitcher,
} from '@/composables/useBrandSwitcher';
import router from '@/router';
import { getRouteBrandLabel } from '@/router/brand';
import { useAuthStore } from '@/stores/auth';
import { useCustomerJourneyStore } from '@/stores/customer-journey';
import { useDatadogStore, viewNameForRoute } from '@/stores/datadog';
import { useFlagStore } from '@/stores/flag';
import { useHubspotStore } from '@/stores/hubspot';
import { useIdentityStore } from '@/stores/identity';
import { useImpersonatorStore } from '@/stores/impersonator';
import { usePageTitleStore } from '@/stores/page-title';
import pinia from '@/stores/pinia';
import { usePlatformStore } from '@/stores/platform';
import { useTokenStore } from '@/stores/token';
import { useTrackingStore } from '@/stores/tracking';
import { browserStorageSetItem, browserStorageSetSessionItem } from '@/utils/browserStorage';
import { isE2E } from '@/utils/e2e';
import {
  clearGlobalModals,
  getGlobalModals,
  popGlobalModal,
  pushGlobalModal,
} from '@/utils/globalModals';
import { getLegacyMediaRedirect } from '@/utils/legacyMediaRedirect';
import { logger } from '@/utils/logger';
import { stringifyQuery } from '@/utils/query';
import { AFTER_LOGIN_NEXT_KEY, loginRedirect } from '@/utils/redirect';

const modalComponents = {
  SocialListeningUpgradeModal: () =>
    import('@/app/settings/components/SocialListeningUpgradeModal.vue'),
  CampaignCreate: () => import('@/app/campaigns/pages/CampaignEdit.vue'),
  InstagramCommentPopup: () => import('@/app/community/components/InstagramCommentPopup.vue'),
  TiktokCommentPopup: () => import('@/app/community/components/TiktokCommentPopup.vue'),
  FacebookCommentPopup: () => import('@/app/community/components/FacebookCommentPopup.vue'),
  TwitterRepliesPopup: () => import('@/app/community/components/twitter/TwitterRepliesPopup.vue'),
  LinkedinCommentPopup: () => import('@/app/community/components/LinkedinCommentPopup.vue'),
  YouTubeCommentPopup: () => import('@/app/community/components/youtube/YouTubeCommentPopup.vue'),
  UploadPopup: () => import('@/app/library/pages/UploadPopup.vue'),
  InstagramUgcCommentPopup: () =>
    import('@/app/analytics/instagram/components/UGC/InstagramUgcCommentPopup.vue'),
  StoryBoardPopup: () => import('@/app/dashboards/components/StoryBoardPopup.vue'),
  SchedulerURLRedirect: () =>
    import('@/app/scheduler/components/EditPost/Layout/SchedulerURLRedirect.vue'),
};

function routeUsesAuthOnlyShell(route) {
  return route?.matched?.some((record) => record.meta?.authOnlyShell);
}

function isImpersonatedUserTokenExpired() {
  const impersonatorStore = useImpersonatorStore(pinia);
  return impersonatorStore.isImpersonating && impersonatorStore.isImpersonatedUserExpired;
}

// True while the current navigation itself changed the brand (cross-brand URL
// navigation, e.g. a Vision AI redirect widget). Raises `isBrandSwitching` so
// brand watchers that navigate (community filter sync, search clearing, etc.)
// stay quiet instead of cancelling the in-flight navigation. Cleared once the
// navigation settles.
let navigationBrandSwitchActive = false;
// Brand to put back if a later guard aborts a URL-driven brand change.
let brandLabelBeforeNavigation = null;

function redirectOntoSwitchTarget(to) {
  const target = brandSwitchTarget();
  const brandLabel = getRouteBrandLabel(to);
  if (!target || !brandLabel || brandLabel === target || !to.name) {
    return undefined;
  }
  return {
    name: to.name,
    params: { ...to.params, brandLabel: target },
    query: to.query,
    hash: to.hash,
  };
}

function routeBrandChange(to, from) {
  const authStore = useAuthStore(pinia);
  const brandLabel = getRouteBrandLabel(to);

  if (
    brandLabel &&
    brandLabel !== authStore.currentBrandLabel &&
    from.name !== DASHBOARD_ROUTE_NAMES.ID
  ) {
    return brandLabel;
  }
  return null;
}

// The first guard sets the store before later guards run; a navigation that then fails
// leaves the URL on `from`, so the store must follow it back.
function restoreBrandOfRoute(route) {
  const brandLabel = getRouteBrandLabel(route);
  const authStore = useAuthStore(pinia);
  if (brandLabel && brandLabel !== authStore.currentBrandLabel) {
    authStore.setCurrentBrand({ brandLabel });
  }
}

export function restoreBrandAfterAbortedNavigation(to, from, failure) {
  if (isNavigationFailure(failure, NavigationFailureType.aborted)) {
    restoreBrandOfRoute(from);
  }
}

export function restoreBrandAfterNavigationError(_error, _to, from) {
  restoreBrandOfRoute(from);
}

// The switcher raises `isBrandSwitching` and runs the before-switch guards itself; other in-app
// navigations (links, back/forward) need both here. Initial loads have nothing mounted to react.
function isUrlDrivenBrandChange(from) {
  return !!from.name && !useBrandSwitcher().isBrandSwitching.value;
}

function setCurrentBrandForRoute(to, from) {
  const brandLabel = routeBrandChange(to, from);
  if (!brandLabel) return;

  const authStore = useAuthStore(pinia);
  if (isUrlDrivenBrandChange(from)) {
    // globalBeforeEach re-applies the route brand after impersonating; keep the first label.
    brandLabelBeforeNavigation ??= authStore.currentBrandLabel;
    navigationBrandSwitchActive = true;
    setNavigationBrandSwitch(true);
  }
  authStore.setCurrentBrand({ brandLabel });
}

function settleNavigationBrandSwitch({ restore }) {
  if (!navigationBrandSwitchActive) return;

  const authStore = useAuthStore(pinia);
  if (
    restore &&
    brandLabelBeforeNavigation &&
    brandLabelBeforeNavigation !== authStore.currentBrandLabel
  ) {
    void authStore.setCurrentBrand({ brandLabel: brandLabelBeforeNavigation });
  }
  navigationBrandSwitchActive = false;
  brandLabelBeforeNavigation = null;
  setNavigationBrandSwitch(false);
}

export function clearNavigationBrandSwitch(to, from, failure) {
  settleNavigationBrandSwitch({
    restore: isNavigationFailure(failure, NavigationFailureType.aborted),
  });
}

export function rollBackNavigationBrandSwitch() {
  settleNavigationBrandSwitch({ restore: true });
}

function redirectLegacyURL(to) {
  const authStore = useAuthStore(pinia);
  // Assign redirect to brand
  let brandLabel = authStore.currentBrand?.label;
  let { query } = to;
  const { href } = to;
  // sometimes an image url has additional parameters that result in a broken query
  if (href?.startsWith('/library/discover')) {
    const url = href.match(/\?(url=)([^&]+)(\?.+)?$/) || [];
    if (url.length >= 3) {
      query.url = url[2];
      query.urlParams = url[3] ?? '';
    }
  }
  // If this is a report, and there's a brand query param, we need to use it as the brand
  // label instead of what's stored in Pinia -- this is to support redirects from admin
  // before it has been updated to use the new URLs, and can be removed once it has been updated.
  if (to.matched.some((route) => route.meta.report) && query.brand) {
    const { brand, ...remainingQuery } = query;
    brandLabel = brand;
    query = remainingQuery;
  }

  return { path: `/${brandLabel}${to.path}`, query, hash: to.hash, replace: true };
}

function openGlobalModal(to, from) {
  const modal = to.meta?.modal;
  // Use rootPath defined in the route meta, otherwise get last available matched path
  let rootPath = to.meta?.rootPath ? to.meta.rootPath : to.matched[to.matched.length - 2]?.path;
  const brandLabel = getRouteBrandLabel(to);
  if (rootPath && brandLabel) {
    rootPath = rootPath.replace(/:brandLabel[^/]*/, brandLabel);
  }
  const coldLoadModal = !from.name;
  const fromForwardButton = window.location.pathname === to.fullPath && !coldLoadModal;
  const fromLogin = from.name === 'auth.login';
  const { replaceModal: _, ...restOfQuery } = to.query ?? {};
  const shouldReplace = to.query?.replaceModal && !coldLoadModal;

  if (coldLoadModal || fromLogin) {
    // push the root path onto the history stack
    // so we have something to go back to when closing the modal
    window.history.pushState({}, null, rootPath);
  }

  // push a state onto the browser history stack to allow the user to see the modal's url,
  // and go back to dismiss
  if (!fromLogin && !fromForwardButton) {
    let { path } = to;

    if (!isEmpty(restOfQuery)) {
      path = `${path}?${stringifyQuery(restOfQuery)}`;
    }
    window.history[shouldReplace ? 'replaceState' : 'pushState'](
      { modalCount: getGlobalModals().length + 1 },
      null,
      path,
    );
  }

  if (shouldReplace) {
    popGlobalModal();
  }

  // Dynamically load component
  const dynamicModalComponent = defineAsyncComponent(modalComponents[modal.component]);

  pushGlobalModal({
    component: dynamicModalComponent,
    props: {
      ...modal.props,
      ...restOfQuery,
      ...to.params,
      ...to.query,
      hash: to.hash?.replace?.(/^#/, ''),
    },
  });

  // Prevent legacy route redirect from duplicating modals
  if (to.matched.some((route) => route.meta.legacy)) {
    popGlobalModal();
  }

  // if this is a cold load of the modal, we need to load the initial route.
  // Otherwise, cancel navigation since the URL was updated via pushState.
  if (!coldLoadModal && !fromLogin) {
    return false;
  }
  return undefined;
}

// this navigation guard enables global modals with their own URL
// (see docs/reference/global-modals.md for details)
function handleGlobalModalRouting(to, from) {
  if (to.meta?.modal) {
    return openGlobalModal(to, from);
  }

  // if navigating anywhere without a modal specified, close all global modals.
  clearGlobalModals();
  return undefined;
}

async function handlePlatformConnection(to) {
  const authStore = useAuthStore(pinia);
  const platformStore = usePlatformStore(pinia);
  const { token, brandId } = to.params;
  browserStorageSetItem('oauthBrandToken', token);
  const brandParams = { ids: brandId };
  const platform =
    to.meta.platform === 'pinterest_v5' ? 'pinterest_v5' : snakeCase(to.meta.platform);
  const tokenKey = `connect_${platform}_token`;
  brandParams[tokenKey] = token;
  if (
    to.meta.platform === enumTypes.INSTAGRAM_INFLUENCER ||
    to.meta.platform === enumTypes.TIKTOK_CREATOR
  ) {
    brandParams.is_influencer = true;
  }

  authStore.setCurrentBrandByAttributes({ brand: { id: brandId } });
  try {
    const brands = await authStore.getBrands(brandParams);
    authStore.setCurrentBrandByAttributes({ brand: brands[0] });
    await platformStore.getPlatformConnectionsByBrandToken({
      brandId,
      brandToken: token,
      platformType: platform,
    });
  } catch {
    // fail here most likely meaning the brand_token is bad, so we redirect to login
    // to prevent the connect page from being exposed to public who doesn't have valid token
    return { name: 'auth.login' };
  }

  const platformConnectionsByBrandToken = platformStore.platformConnectionsByBrandToken;
  // redirect to success page for connected brands, otherwise go to connection
  try {
    if (platformConnectionsByBrandToken[0].status === 'connected') {
      const platformType =
        to.meta.platform === enumTypes.FACEBOOK_ANALYTICS ||
        to.meta.platform === enumTypes.FACEBOOK_SANDBOX
          ? 'facebook'
          : to.meta.platform;
      if (
        to.meta.platform !== enumTypes.INSTAGRAM_INFLUENCER &&
        to.meta.platform !== enumTypes.TIKTOK_CREATOR
      ) {
        const connectionSuccessName = `connect.${camelCase(platformType)}.success`;
        return { name: connectionSuccessName };
      }
    } else if (platformConnectionsByBrandToken[0].id) {
      const connectionId = platformConnectionsByBrandToken[0].id;
      browserStorageSetItem('oauthConnectionId', connectionId);
    }
  } catch {
    // proceed to route
  }
  return undefined;
}

export async function skipLoginForReports(to) {
  const { token } = to.query;
  // No share token → let handleReportRoutes fall back to the session check.
  if (!token) return undefined;

  const authStore = useAuthStore(pinia);
  const brand = to.query?.brand ?? getRouteBrandLabel(to);
  try {
    await authStore.loginByTokenAndBrandLabel({ token, brand });
  } catch (error) {
    // Any token-login failure → redirect to login rather than re-throwing (strands the user).
    logger.error('Report share-token login failed', { brand, error });
    return { name: 'auth.login' };
  }
  return undefined;
}

function handleReportRoutes(to) {
  const authStore = useAuthStore(pinia);
  if (to.matched.some((route) => route.meta.legacy)) {
    return redirectLegacyURL(to);
  }
  if (authStore.isLoggedIn) {
    return undefined;
  }
  return { name: 'auth.login' };
}

async function handleAuthenticatedRoutes(to) {
  const authStore = useAuthStore(pinia);
  if (authStore.isLoggedIn) {
    authStore.checkRouteAccess({ route: to });

    // If route is a legacy route, redirect the user
    if (to.matched.some((route) => route.meta.legacy)) {
      return redirectLegacyURL(to);
    }
    return undefined;
  }
  authStore.setRouteUnauthorized({ unauthorized: true });
  await authStore.logout();
  browserStorageSetSessionItem(
    AFTER_LOGIN_NEXT_KEY,
    getLegacyMediaRedirect(to)?.fullPath ?? to.fullPath,
  );
  return { name: 'auth.login' };
}

export async function handleRedirectsBasedOnPlatformConnections(to) {
  if (routeUsesAuthOnlyShell(to)) {
    return undefined;
  }

  // Disable this check for e2e tests
  if (isE2E()) {
    return undefined;
  }

  const targetRouteAllowedWhenNoConnections = to.matched.some(
    (route) => !!route?.meta?.allowWhenNoConnections,
  );

  if (targetRouteAllowedWhenNoConnections) {
    return undefined;
  }

  const platformStore = usePlatformStore(pinia);
  if (platformStore.platformConnectionsInitialized === false) {
    await platformStore.getPlatformConnections();
  }

  const authStore = useAuthStore(pinia);
  const customerJourneyStore = useCustomerJourneyStore(pinia);
  const brandUserNeedsToConnect =
    customerJourneyStore.isProductLedGrowTrial &&
    !authStore.canAccessMultipleBrands &&
    !platformStore.hasOneRequiredBrandPlatformConnection &&
    !authStore.canOnlyAccessInactiveBrand;

  if (brandUserNeedsToConnect && to?.name !== 'onboardingFlowPage') {
    return { name: 'onboardingFlowPage' };
  }
  return undefined;
}

export async function globalBeforeEach(to, from) {
  const tokenStore = useTokenStore(pinia);
  const identityStore = useIdentityStore(pinia);
  const authStore = useAuthStore(pinia);

  const { dashToken } = to?.query ?? {};
  if (!tokenStore.token && dashToken) {
    tokenStore.setToken({ token: dashToken });
  }

  const { ssoToken } = to?.query ?? {};
  if (!tokenStore.token && ssoToken) {
    try {
      prefetchLandingPage();
      await authStore.exchangeSsoToken({ ssoToken });
    } catch {
      return { name: 'auth.login' };
    }
  }

  if (!tokenStore.hasAttemptedTokenRefresh && !tokenStore.token) {
    await tokenStore.refreshToken();
  }

  // If we don't have the  user's identity, try to fetch it from the /self endpoint and put it in the store.
  if (tokenStore.token && !authStore.hasAttemptedIdentification && !authStore.isLoggedIn) {
    await authStore.checkForIdentity();
  }

  // This prevents users from avoiding the setup by pasting another page's url
  if (
    router &&
    identityStore.identity?.invitation_status === 'accepted' &&
    to.name !== 'account.setup'
  ) {
    loginRedirect(router);
  }

  // Skip login page for reports - these are only accessed by the PDF service which provides
  // a brand and access token via query params.
  if (to.matched.some((route) => route.meta.report)) {
    const skipResult = await skipLoginForReports(to, from);
    if (skipResult) return skipResult;
  }

  // If a user's ID is provided in query params, initialize impersonation
  const impersonatorStore = useImpersonatorStore(pinia);
  const { impersonateUserId } = to?.query ?? {};
  if (
    impersonateUserId &&
    impersonateUserId !== impersonatorStore.impersonatedUserId &&
    (identityStore.isSuperAdmin || impersonatorStore.isImpersonating)
  ) {
    await impersonatorStore.impersonateUserById({ userId: impersonateUserId });
    const identified = await authStore.checkForIdentity();
    if (!identified) {
      await authStore.logout();
      browserStorageSetSessionItem(
        AFTER_LOGIN_NEXT_KEY,
        getLegacyMediaRedirect(to)?.fullPath ?? to.fullPath,
      );
      return { name: 'auth.login' };
    }
    setCurrentBrandForRoute(to, from);
  }

  if (isImpersonatedUserTokenExpired()) {
    await authStore.logout();
    return false;
  }

  if (!from?.name && (to?.query?.cancelled || to?.query?.success)) {
    trackSubscriptionUpdated({ lookupKey: to?.query?.price, success: !!to.query?.success });
  }

  if (
    (to?.meta?.shared && !authStore.isLoggedIn) ||
    (!to?.meta?.shared && identityStore.isShared)
  ) {
    authStore.reset();
    return { name: 'shared.link-unavailable', query: {}, replace: true };
  }

  const toBrandLabel = getRouteBrandLabel(to);
  if (to.path === '/' && to.matched.length > 1 && toBrandLabel && !to.path.includes(toBrandLabel)) {
    const { name, params, query } = to;
    return { name, params, query, replace: true };
  }

  // If the route is for one of our "Connect Your <Platform> Account" pages (e.g.
  // /brand-connect-facebook/<brand-id>/<token>), check to see if the brand is already connected
  // and show either the connection success page or the connection prompt.
  // TODO: Move this out of the global route guard and into a per-route guard or in-component
  // guard. See https://router.vuejs.org/guide/advanced/navigation-guards.html#per-route-guard.
  if (to.matched.some((route) => route.meta.connection)) {
    return handlePlatformConnection(to, from);
  }
  if (to.matched.some((route) => route.meta.report)) {
    return handleReportRoutes(to, from);
  }

  // If the route is login protected and the user isn't logged in, show them the login page.
  if (to.matched.some((route) => route.meta.requiresAuth)) {
    return handleAuthenticatedRoutes(to, from);
  }
  return undefined;
}

// Must run after `globalBeforeEach`: `waitForUserFlags` only resolves once the flag client has
// a real targeting key, which comes from the identity that guard fetches. Registered earlier it
// sees `isLoggedIn === false` on a cold load and skips, so routes render before their flags.
export async function waitForFlags(to) {
  if (routeUsesAuthOnlyShell(to)) {
    return;
  }

  const authStore = useAuthStore(pinia);
  if (authStore.isLoggedIn) {
    try {
      await useFlagStore(pinia).waitForUserFlags;
    } catch {
      // proceed to route even if flag fetch fails
    }
  }
}

export async function updateCurrentBrandIfRequired(to, from) {
  const switchRedirect = redirectOntoSwitchTarget(to);
  if (switchRedirect) {
    return switchRedirect;
  }
  if (
    routeBrandChange(to, from) &&
    isUrlDrivenBrandChange(from) &&
    !(await runBeforeBrandSwitchGuards())
  ) {
    return false;
  }
  setCurrentBrandForRoute(to, from);
  return undefined;
}

function refreshHubSpotCallToAction(to, from) {
  if (routeUsesAuthOnlyShell(to)) {
    return;
  }

  if (to?.path !== from?.path) {
    const hubSpotStore = useHubspotStore(pinia);
    hubSpotStore.refreshCallToAction();
  }
}

function refreshHubSpotToken(to) {
  if (routeUsesAuthOnlyShell(to)) {
    return;
  }
  void useHubspotStore(pinia).refreshTokenIfStale();
}

function trackHubSpotEvent(to, from) {
  if (routeUsesAuthOnlyShell(to)) {
    return;
  }

  if (to?.path !== from?.path) {
    const hubSpotStore = useHubspotStore(pinia);
    hubSpotStore.trackEvent({ eventName: 'trackPageView' });
  }
}

// Approved tab-title copy per route section (first dotted segment); mirror usePrimaryNavigationMenu / useUtilityNavigationMenu.
const NAV_SECTION_TITLES = {
  vision: 'Vision',
  dashboards: 'Dashboards',
  analytics: 'Analytics',
  creators: 'Creators',
  campaigns: 'Campaigns',
  community: 'Community',
  competitors: 'Competitors',
  scheduler: 'Scheduler',
  library: 'Library',
  listening: 'Listening',
  'constellation-pro': 'Constellation Pro',
  settings: 'Settings',
};

// Per-platform analytics routes all live under the "Analytics" button (mirrors PrimaryNavigationMenu selectionMap).
const ANALYTICS_ROUTE_SECTIONS = new Set([
  'instagram',
  'facebook',
  'tiktok',
  'twitter',
  'threads',
  'youtube',
  'linkedin',
  'pinterest',
  'snapchat',
]);

// Full route names that get their own nav button distinct from their section root.
const EXACT_ROUTE_TITLES = {
  'settings.billing': 'Billing and Plan',
};

// Approved nav label for a route, or null when it maps to no nav button (landing, auth, not-found).
export function resolveNavSectionTitle(to) {
  const name = to?.name;
  if (!name) return null;
  if (EXACT_ROUTE_TITLES[name]) return EXACT_ROUTE_TITLES[name];
  const root = name.split('.')[0];
  const section = ANALYTICS_ROUTE_SECTIONS.has(root) ? 'analytics' : root;
  return NAV_SECTION_TITLES[section] ?? null;
}

// Publish the section half of the tab title; the active secondary-nav item supplies the other half.
export function updatePageTitle(to) {
  usePageTitleStore(pinia).setSection(resolveNavSectionTitle(to));
}

export function handleExpiredTrialAccess(to) {
  if (routeUsesAuthOnlyShell(to)) {
    return undefined;
  }

  if (['settings.billing', 'settings.socialPlatform', 'auth.logout', 'plans'].includes(to?.name)) {
    return undefined;
  }
  const authStore = useAuthStore(pinia);
  if (authStore.canOnlyAccessInactiveBrand) {
    return { name: 'settings.billing', params: { brandLabel: authStore.currentBrandLabel } };
  }
  return undefined;
}

/** Initializes before page components mount, including routes that skip App.vue. */
export function initDatadog(to, from, failure) {
  if (failure || to.meta?.report) return;
  try {
    useDatadogStore(pinia).init();
  } catch {
    // An SDK failure must not reject the initial navigation and prevent app mounting.
    logger.warn('Unable to initialize Datadog');
  }
}

/** Names the RUM view vue-router's history push just started; browser logs read it from RUM too. */
export function nameDatadogView(to, from, failure) {
  // A failed navigation never pushed history, so the current view is still `from`.
  if (failure) return;
  useDatadogStore(pinia).setViewName(viewNameForRoute(to));
}

/**
 * Wires stores and global guards into the app router. Call once from app bootstrap, before
 * `app.use(pinia)` and `app.use(router)` — kept out of module scope so importing this file
 * creates no stores. See main.js for why the call sits before `app.use(pinia)`.
 */
export function installRouterLifecycle() {
  // Eager so the flag client starts fetching before the first navigation reaches `waitForFlags`.
  useFlagStore(pinia);
  // Before the guards below: tracking's afterEach hooks have always run first.
  useTrackingStore(pinia).setupRouter(router);

  router.beforeEach(updateCurrentBrandIfRequired);
  router.beforeEach(handleGlobalModalRouting);
  router.beforeEach(globalBeforeEach);
  // Order matters — see `waitForFlags`. It depends on the identity `globalBeforeEach` resolves.
  router.beforeEach(waitForFlags);
  router.beforeEach(handleExpiredTrialAccess);
  router.beforeEach(handleRedirectsBasedOnPlatformConnections);
  router.afterEach(refreshHubSpotCallToAction);
  router.afterEach(trackHubSpotEvent);
  router.afterEach(refreshHubSpotToken);
  router.afterEach(updatePageTitle);
  // Before naming, so the first view's name lands on an initialized SDK.
  router.afterEach(initDatadog);
  router.afterEach(nameDatadogView);
  router.afterEach(restoreBrandAfterAbortedNavigation);
  router.afterEach(clearNavigationBrandSwitch);
  router.onError(restoreBrandAfterNavigationError);
  router.onError(rollBackNavigationBrandSwitch);
}

export default router;
